import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { toggleEditMode } from '../../../shared/utils';
import { CustomErrorStateMatcher, CustomValidator } from '../../../shared/validation';
import { IPartner } from '../../models/partner';
import { DataService } from '../../service/data.service';
import { VersicherungsnehmerListDialogService } from '../../service/partner-list-dialog.service';
import { PopupService } from '../../service/popup.service';
import { AdresseComponent } from '../adresse/adresse.component';
import { KeyValue } from '@angular/common';

@Component({
  selector: 'app-partner',
  templateUrl: './partner.component.html',
  styleUrls: ['./partner.component.scss']
})
export class PartnerComponent implements OnInit, OnChanges {

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  privatperson: boolean;

  @Input()
  versicherungsnehmer: boolean;

  @Input()
  idprefix: string;

  @Input()
  showGroup = true;

  @Input()
  showEinzelunternehmen = false;

  @Input()
  anredenPrivatperson = false;

  @Input()
  student = false;

  @Input()
  _geburtsdatum: Date;

  @Input()
  expertMode: boolean;

  @Input()
  validateAlter = (_value: Date): boolean => { return true; };

  @Output()
  partnerChangedEvent = new EventEmitter<void>();

  geburtsdatumDisabled = false;

  matcher = new CustomErrorStateMatcher();

  @ViewChild('_adresse') adresseComponent: AdresseComponent;

  constructor(public dataService: DataService, public translateService: TranslateService, public popupService: PopupService,
    public versicherungsnehmerListDialogService: VersicherungsnehmerListDialogService) { }

  static createFormGroup(fb: UntypedFormBuilder, expertMode: boolean, emailRequired: boolean = true, geburtsdatumRequired: boolean = true) {
    return fb.group({
      anrede: new UntypedFormControl('', Validators.required),
      firmenname: new UntypedFormControl('', Validators.required),
      titelPrefix: new UntypedFormControl(''),
      vorname: new UntypedFormControl('', [Validators.required, CustomValidator.firstLastName]),
      nachname: new UntypedFormControl('', [Validators.required, CustomValidator.firstLastName]),
      titelSuffix: new UntypedFormControl(''),
      adresse: AdresseComponent.createFormGroup(fb),
      emailGroup: fb.group({
        email: new UntypedFormControl('', emailRequired && !expertMode ? [CustomValidator.validateEmail, Validators.required] : CustomValidator.validateEmailOptional),
        confirm: new UntypedFormControl('', emailRequired && !expertMode ? [CustomValidator.validateEmail, Validators.required] : CustomValidator.validateEmailOptional),
      }, {
        validator: [expertMode ? emailRequired ? CustomValidator.matchEmailExpert : CustomValidator.matchEmailExpertOptional :
          emailRequired ? CustomValidator.matchEmail : CustomValidator.matchEmailOptional]
      }),
      telefonnummer: new UntypedFormControl(''),
      geburtsdatum: new UntypedFormControl('', geburtsdatumRequired ?
        [Validators.required, CustomValidator.validateGeburtsdatum] : CustomValidator.validateGeburtsdatum),
    });
  }

  ngOnInit(): void {
    this.onValueChanged();
    this.onStatusChanged();
    this.toggleEditMode();
  }

  privatpersonChanged() {
    this.geburtsdatumDisabled = false;
    this.toggleEditMode();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.privatperson && changes.privatperson.currentValue !== changes.privatperson.previousValue ||
      changes.showEinzelunternehmen && changes.showEinzelunternehmen.currentValue !== changes.showEinzelunternehmen.previousValue) {
      if (this.getAnreden().size === 1) {
        this.anrede.setValue(this.getAnreden().keys().next().value);
      } else if (!this.getAnreden().has(this.anrede.value)) {
        this.anrede.setValue(null);
      }
      this.toggleEditMode();
    }
    if (changes.privatperson && changes.privatperson.currentValue !== changes.privatperson.previousValue) {
      this.privatpersonChanged();
    }
    if (changes.showGroup && changes.showGroup.currentValue !== changes.showGroup.previousValue) {
      this.toggleEditMode();
    }
    if (changes._geburtsdatum && changes._geburtsdatum.currentValue?.toDateString() !== changes._geburtsdatum.previousValue?.toDateString()) {
      this.geburtsdatum.setValue(moment(this._geburtsdatum));
      this.geburtsdatum.disable();
    }
  }

  get showFirmenname() {
    return (this.privatperson === false || this.showEinzelunternehmen === true) && this.showGroup;
  }
  get showName() {
    return (this.privatperson === true || (this.privatperson === false && this.showEinzelunternehmen === true)) && this.showGroup;
  }
  get showGeburtsdatum() {
    return this.showName;
  }
  get showAnrede() {
    return !this.student && this.showGroup;
  }
  get showEmailGroup() {
    return this.showAnrede;
  }

  get anrede() { return this.formGroup.get('anrede'); }
  get firmenname() { return this.formGroup.get('firmenname'); }
  get titelPrefix() { return this.formGroup.get('titelPrefix'); }
  get vorname() { return this.formGroup.get('vorname'); }
  get nachname() { return this.formGroup.get('nachname'); }
  get titelSuffix() { return this.formGroup.get('titelSuffix'); }
  get adresse() { return this.formGroup.get('adresse'); }
  get emailGroup() { return this.formGroup.get('emailGroup'); }
  get email() { return this.emailGroup.get('email'); }
  get confirmEmail() { return this.emailGroup.get('confirm'); }
  get telefonnummer() { return this.formGroup.get('telefonnummer'); }
  get geburtsdatum() { return this.formGroup.get('geburtsdatum'); }

  getAnreden() {
    if (this.privatperson === true || this.anredenPrivatperson || this.showEinzelunternehmen) {
      return new Map([
        [1, 'Frau'],
        [2, 'Herr'],
        [3, 'Divers']
      ]);
    }
    if (this.privatperson === false) {
      return new Map([
        [4, 'Firma']
      ]);
    }
    return new Map();
  }

  toggleEditMode(): void {
    toggleEditMode(this.anrede, this.showAnrede);
    toggleEditMode(this.firmenname, this.showFirmenname);
    toggleEditMode(this.vorname, this.showName);
    toggleEditMode(this.nachname, this.showName);
    toggleEditMode(this.email, this.showEmailGroup);
    toggleEditMode(this.confirmEmail, this.showEmailGroup);
    toggleEditMode(this.telefonnummer, this.showGroup);
    toggleEditMode(this.geburtsdatum, this.showGeburtsdatum, this.geburtsdatumDisabled);
  }

  onStatusChanged(): void {
    this.geburtsdatum.statusChanges.subscribe(() => {
      this.geburtsdatumDisabled = this.geburtsdatum.disabled;
    });
  }

  onPrivatpersonChanged(privatperson: boolean) {
    this.privatperson = privatperson;
    this.privatpersonChanged();
  }

  onValueChanged(): void {
    this.anrede.valueChanges.subscribe(() => {
      if (this.anrede.value) {
        const privatperson = this.anrede.value === 5 || this.anrede.value === 4 || this.showEinzelunternehmen ? false : true;
        if (privatperson !== this.privatperson) {
          this.onPrivatpersonChanged(privatperson);
        }
      }
    });
  }

  setPartner(partner: IPartner) {
    this.anrede.setValue(partner.Anrede);
    this.firmenname.setValue(partner.Firmenname);
    this.titelPrefix.setValue(partner.TitelPrefix);
    this.vorname.setValue(partner.Vorname);
    this.nachname.setValue(partner.Nachname);
    this.titelSuffix.setValue(partner.TitelSuffix);
    if (partner.Adresse) {
      this.adresseComponent.setAdresse(partner.Adresse);
    }
    this.email.setValue(partner.Email);
    this.confirmEmail.setValue(partner.Email);
    this.telefonnummer.setValue(partner.Telefonnummer);
    this.geburtsdatum.setValue(moment(partner.Geburtsdatum));
  }

  getPartner(): IPartner {
    return {
      Privatperson: this.privatperson === true || (this.privatperson === false && this.showEinzelunternehmen === true),
      Anrede: this.anrede.value,
      Firmenname: this.firmenname.value,
      TitelPrefix: this.titelPrefix.value ? this.titelPrefix.value : null,
      Vorname: this.vorname.value,
      Nachname: this.nachname.value,
      TitelSuffix: this.titelSuffix.value ? this.titelSuffix.value : null,
      Geburtsdatum: this.geburtsdatum.value,
      Adresse: this.adresseComponent.getAdresse(),
      Telefonnummer: this.telefonnummer.value,
      Email: this.email.value,
    } as IPartner;
  }

  prefixSort = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    if (a.key === 4 && b.key === 3) {
      return -1;
    } {
      return 0;
    }
  };

}
